import { apiBaseUrl, webSessionStorageKey } from "../config";
import type {
  AppData,
  AppointmentCreate,
  AppointmentUpdate,
  NotificationPreferences,
  PairingResponse,
  SessionExchange,
} from "../types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  authenticated = false,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body) headers.set("content-type", "application/json");
  if (authenticated) {
    const token = localStorage.getItem(webSessionStorageKey);
    if (!token) throw new ApiError(401, "unauthorized");
    headers.set("authorization", `Bearer ${token}`);
  }
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers,
  });
  const body = (await response.json().catch(() => ({}))) as Record<
    string,
    unknown
  >;
  if (!response.ok) {
    if (response.status === 401 && authenticated) {
      localStorage.removeItem(webSessionStorageKey);
    }
    throw new ApiError(
      response.status,
      typeof body.error === "string" ? body.error : "request_failed",
    );
  }
  return body as T;
}

export const api = {
  createPairing(deviceName: string, browserName: string) {
    return request<PairingResponse>("/api/v1/web/pairings", {
      method: "POST",
      body: JSON.stringify({ deviceName, browserName }),
    });
  },
  pairingStatus(pairing: PairingResponse) {
    return request<{ status: "pending" | "approved" | "expired" | "consumed" }>(
      `/api/v1/web/pairings/${encodeURIComponent(pairing.pairingId)}` +
        `?secret=${encodeURIComponent(pairing.secret)}`,
    );
  },
  exchangePairing(pairing: PairingResponse) {
    return request<SessionExchange>(
      `/api/v1/web/pairings/${encodeURIComponent(pairing.pairingId)}/exchange`,
      {
        method: "POST",
        body: JSON.stringify({ secret: pairing.secret }),
      },
    );
  },
  loadAppData() {
    return request<AppData>("/api/v1/web/app-data", {}, true);
  },
  createLiveTicket() {
    return request<{ ticket: string; expiresAt: string }>(
      "/api/v1/web/live-ticket",
      { method: "POST" },
      true,
    );
  },
  updateNotificationPreferences(preferences: NotificationPreferences) {
    return request<NotificationPreferences>(
      "/api/v1/web/notification-preferences",
      { method: "PUT", body: JSON.stringify(preferences) },
      true,
    );
  },
  createAppointment(appointment: AppointmentCreate) {
    return request<{ id: string }>(
      "/api/v1/web/appointments",
      { method: "POST", body: JSON.stringify(appointment) },
      true,
    );
  },
  updateAppointment(appointmentId: string, update: AppointmentUpdate) {
    return request<{ updated: boolean }>(
      `/api/v1/web/appointments/${encodeURIComponent(appointmentId)}`,
      { method: "PATCH", body: JSON.stringify(update) },
      true,
    );
  },
  cancelAppointment(appointmentId: string) {
    return request<{ updated: boolean }>(
      `/api/v1/web/appointments/${encodeURIComponent(appointmentId)}`,
      { method: "PATCH", body: JSON.stringify({ status: "cancelled" }) },
      true,
    );
  },
  logout() {
    return request<{ signedOut: boolean }>(
      "/api/v1/web/session/logout",
      { method: "POST" },
      true,
    );
  },
};
